import Vue from 'vue'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import utils from '@/mixins/utils.js'
import store from '@/store/store'
import { vue } from '@/main.js'

/**
 * Retorna os cadastros de uma equipe
 *
 * @param {int} cod_ibge - IBGE do município
 * @param {int} nu_ine - INE da Equipe
 *
 * @returns {Array}
 */
export async function getCadastrosPorEquipe(cod_ibge, nu_ine) {
  try {
    const { data } = await Vue.prototype.$api.get(
      `/app/media-atendimentos-por-pessoa-emulti/${cod_ibge}/equipe/${nu_ine}`
    )
    return data
  } catch (err) {
    console.log(err)
  }
}

/**
 * Retorna os cadastros por microárea
 *
 * @param {*} cod_ibge
 * @param {*} nu_ine
 * @param {*} micro_area
 *
 * @returns {Array}
 */
export async function getCadastrosPorMicroArea(cod_ibge, nu_ine, micro_area) {
  try {
    const { data } = await Vue.prototype.$api.get(
      `/app/media-atendimentos-por-pessoa-emulti/${cod_ibge}/equipe/${nu_ine}/microarea/${micro_area}`
    )
    return data
  } catch (err) {
    console.log(err)
  }
}

/**
 * Retorna o nome da cor de acordo com o valor
 * @param {Number} valor - Valor numérico
 * @returns {String}
 */
export function getCorTextoResultado(valor) {
  if (valor >= 60) {
    return 'green-color'
  } else {
    return 'red-color'
  }
}

/**
 * Retorna os pares mês/ano considerados no filtro de período.
 *
 * Inclui o período selecionado e os 3 meses anteriores ao primeiro mês:
 * - Mês: janeiro/2026 → out/2025, nov/2025, dez/2025 e jan/2026
 * - Quadrimestre: 2º (mai–ago) → fev, mar, abr + mai, jun, jul, ago
 *
 * @param {Object|null} mes Mês/período selecionado.
 * @returns {Array<{nu_mes: Number, nu_ano: Number}>}
 */
export function obterPeriodosFiltro(mes) {
  if (!mes) {
    return []
  }

  const mesesSelecionados = (mes.value || []).map(Number)
  const anoSelecionado = Number(mes.nu_ano)

  if (!mesesSelecionados.length) {
    return []
  }

  const periodos = mesesSelecionados.map((nu_mes) => ({
    nu_mes,
    nu_ano: anoSelecionado,
  }))

  const primeiroMes = Math.min(...mesesSelecionados)
  let mesAtual = primeiroMes
  let anoAtual = anoSelecionado

  for (let i = 0; i < 3; i++) {
    mesAtual -= 1
    if (mesAtual < 1) {
      mesAtual = 12
      anoAtual -= 1
    }
    periodos.unshift({ nu_mes: mesAtual, nu_ano: anoAtual })
  }

  return periodos
}

/**
 * Verifica se o mês/ano do registro está no período filtrado.
 *
 * @param {Array<{nu_mes: Number, nu_ano: Number}>} periodos
 * @param {Number|String} nu_mes
 * @param {Number|String} nu_ano
 * @returns {Boolean}
 */
export function periodoInclui(periodos, nu_mes, nu_ano) {
  return periodos.some(
    (periodo) =>
      periodo.nu_mes === Number(nu_mes) && periodo.nu_ano === Number(nu_ano)
  )
}

/**
 * Retorna os atendimentos do cadastro filtrados por período e equipe.
 *
 * @param {Object} item Cadastro.
 * @param {Object|null} mes Mês/período selecionado.
 * @param {Object|null} equipe Equipe selecionada.
 * @returns {Array<Object>}
 */
export function getAtendimentosFiltrados(item, mes = null, equipe = null) {
  let dados = []

  try {
    dados =
      typeof item.dados === 'string' ? JSON.parse(item.dados) : item.dados || []
  } catch (e) {
    dados = []
  }

  if (!Array.isArray(dados)) {
    return []
  }

  const periodos = mes ? obterPeriodosFiltro(mes) : null
  const ineEquipe = equipe?.ine_emulti

  return dados.filter((proc) => {
    if (Number(proc.nm) === 0) {
      return false
    }

    if (
      ineEquipe != null &&
      ineEquipe !== '' &&
      String(proc.nu_ine) != String(ineEquipe)
    ) {
      return false
    }

    if (periodos && !periodoInclui(periodos, proc.nu_mes, proc.nu_ano)) {
      return false
    }

    return true
  })
}

/**
 * Retorna a quantidade de atendimentos da pessoa.
 *
 * Considera o período selecionado e a equipe filtrada.
 *
 * @param {Object} item Cadastro.
 * @param {Object|null} mes Mês/período selecionado.
 * @param {Object|null} equipe Equipe selecionada.
 * @returns {Number} Quantidade de atendimentos.
 */
export function getQuantidadePessoasAtendidas(
  item,
  mes = null,
  equipe = null
) {
  return getAtendimentosFiltrados(item, mes, equipe).length
}

/**
 * Lista completa de atendimentos a partir do JSON em `cadastro.dados`.
 *
 * @param {*} dados
 * @returns {Array}
 */
function parseListaAtendimentos(dados) {
  if (dados == null || dados === '') return []
  try {
    const arr = typeof dados === 'string' ? JSON.parse(dados) : dados
    return Array.isArray(arr) ? arr : []
  } catch {
    return []
  }
}

/**
 * Gera o relatório em PDF.
 *
 * @param {Array} cadastros
 * @param {Object} selectEquipe
 * @param {String} microArea
 * @param {Object|String|Number|null} mes Mês selecionado no filtro.
 */
export function generatePdf(cadastros, selectEquipe, microArea, mes = null) {
  let title_pdf = 'Pessoas atendidas'
  let titlePdfSave = utils.methods.titlePdf('Pessoas atendidas')

  let description_pdf = 'Pessoas atendidas por profissionais da eMulti'
  const headers_pdf = [
    'NOME',
    'NASCIMENTO',
    'MÃE',
    'CPF',
    'CNS',
    'MICROÁREA',
    'CADASTRADO POR',
    'ATENDIMENTOS',
  ]
  const idxAtendimentos = 7

  function linhaPrincipal(c) {
    return [
      c.no_cidadao ?? '',
      (utils.methods.formatDate(c.dt_nascimento) || '') +
        `\n(${c.idade} ${c.idade > 1 ? 'anos' : 'ano'})`,
      c.no_mae ?? '',
      c.nu_cpf ?? '',
      c.nu_cns ?? '',
      c.micro_area ?? '',
      c.no_profissional ?? '',
      (c.atendimentos = getQuantidadePessoasAtendidas(c, mes, selectEquipe)),
    ]
  }

  const estiloHead = {
    fillColor: '#ccc',
    fontSize: 6,
    textColor: '#000',
    halign: 'center',
    cellWidth: 'wrap',
  }
  const estiloBody = {
    halign: 'center',
    fontSize: 6,
  }

  const estiloBodyPrincipal = {
    ...estiloBody,
    fillColor: [245, 245, 245],
  }

  const optsTabelaPrincipal = {
    theme: 'plain',
    styles: { lineWidth: 0 },
    tableLineWidth: 0,
  }

  const corBordaExpandida = [228, 228, 228]
  const optsTabelaExpandida = {
    theme: 'grid',
    styles: {
      lineWidth: 0.05,
      lineColor: corBordaExpandida,
    },
    tableLineWidth: 0,
  }

  let doc = new jsPDF({
    orientation: 'l',
    unit: 'pt',
    format: 'a4',
    ...utils.methods.encryptionPdf(),
  })

  utils.methods
    .fileToBase64(store.state.infoMunicipio.brasao)
    .then((base64data) => {
      // Header
      let verticalPosition = utils.methods.headerPdf(
        doc,
        base64data,
        title_pdf,
        description_pdf,
        200,
        'l'
      )

      const dadosCabecalho = []

      const nomeMes = utils.methods.getDescricaoMes(mes)

      if (nomeMes) {
        dadosCabecalho.push({
          label: 'Período',
          value: nomeMes,
        })
      }

      dadosCabecalho.push(
        {
          label: 'Equipe',
          value: selectEquipe.no_equipe,
        },
        {
          label: 'CNES',
          value: selectEquipe.nu_cnes,
        },
        {
          label: 'INE',
          value: selectEquipe.nu_ine,
        },
        {
          label: 'Microárea',
          value: microArea,
        }
      )

      verticalPosition = utils.methods.bodyPdf(
        doc,
        dadosCabecalho,
        verticalPosition,
        'l'
      )

      let currentY = verticalPosition

      if (!cadastros.length) {
        doc.autoTable({
          head: [headers_pdf],
          body: [['—', '—', '—', '—', '—', '—', '—', '—']],
          headStyles: estiloHead,
          bodyStyles: estiloBodyPrincipal,
          ...optsTabelaPrincipal,
          startY: currentY,
          showHead: 'firstPage',
        })
      } else {
        for (let i = 0; i < cadastros.length; i++) {
          const c = cadastros[i]
          const optsPrincipal = {
            body: [linhaPrincipal(c)],
            headStyles: estiloHead,
            bodyStyles: estiloBodyPrincipal,
            ...optsTabelaPrincipal,
            startY: currentY,
            showHead: i === 0 ? 'firstPage' : 'never',
            willDrawCell: function (data) {
              if (data.row.section === 'head') {
                doc.setTextColor(0, 0, 0)
                return
              }
              const num_med_atend_pessoa = data.row.raw[idxAtendimentos]
              if (num_med_atend_pessoa >= 1 && num_med_atend_pessoa <= 2) {
                doc.setTextColor(255, 165, 0)
              } else if (num_med_atend_pessoa >= 3) {
                doc.setTextColor(2, 99, 57)
              } else {
                doc.setTextColor(0, 0, 0)
              }
            },
          }
          if (i === 0) optsPrincipal.head = [headers_pdf]
          doc.autoTable(optsPrincipal)
          currentY = doc.autoTable.previous.finalY

          const lista = parseListaAtendimentos(c.dados)
          const corpoDetalhe = lista.map((a) => [
            a.dt_atendimento != null
              ? utils.methods.formatDate(a.dt_atendimento)
              : '',
            a.tp_atendimento ?? '',
            a.nu_ine ?? '',
          ])

          doc.autoTable({
            head: [['Data', 'Tipo', 'Equipe']],
            body:
              corpoDetalhe.length > 0
                ? corpoDetalhe
                : [['', 'Sem registros de atendimento', '']],
            headStyles: {
              ...estiloHead,
              fillColor: '#e8e8e8',
              halign: 'center',
              valign: 'middle',
              lineWidth: 0,
            },
            bodyStyles: {
              ...estiloBody,
              halign: 'center',
              valign: 'middle',
              lineWidth: 0.05,
              lineColor: corBordaExpandida,
            },
            columnStyles: {
              0: { cellWidth: 100, halign: 'center' },
              1: { cellWidth: 'auto', halign: 'center' },
              2: { cellWidth: 100, halign: 'center' },
            },
            ...optsTabelaExpandida,
            startY: currentY,
            willDrawCell: function (data) {
              doc.setTextColor(0, 0, 0)
            },
          })
          currentY = doc.autoTable.previous.finalY + 14
        }
      }

      utils.methods.footerPdf(doc, doc.autoTable.previous.finalY, 250, 600, 'l')

      doc.save(titlePdfSave + '.pdf')

      vue.$toast.success('PDF GERADO COM SUCESSO!', 'Sucesso:', {
        position: 'topRight',
      })
    })
    .catch((error) => {
      console.error(error)
      vue.$toast.error('ERRO AO GERAR PDF!', 'Erro:', {
        position: 'topRight',
      })
    })
}

function getMesAnoAtual() {
  const hoje = new Date()
  const mesAtual = hoje.getMonth() + 1
  const anoAtual = String(hoje.getFullYear())

  return `${mesAtual}/${anoAtual}`
}

function montarResultado(dados, tipoResultado = 'quadrimestral') {
  if (tipoResultado === 'mensal') {
    return {
      numerador: dados.numerador,
      denominador: dados.denominador,
      resultado: dados.resultado,
      classificacao: dados.classificacao,
    }
  }

  return {
    numerador: dados.numerador_quad,
    denominador: dados.denominador_quad,
    resultado: dados.resultado_quad,
    classificacao: dados.classificacao_quad,
  }
}

export function percentualMunicipio(
  dadosDaTabelaResultado,
  tipoResultado = 'quadrimestral'
) {
  const mesAnoAtual = getMesAnoAtual()

  const dadosDoMunicipio = dadosDaTabelaResultado.find(
    (item) =>
      item.nivel == 1 &&
      item.ibge === store.state.cod_ibge &&
      item.indicador == 44 &&
      item.mes_ano === mesAnoAtual
  )

  if (!dadosDoMunicipio) {
    return {
      numerador: 0,
      denominador: 0,
      resultado: 0,
      classificacao: '',
    }
  }

  const dados = utils.methods.parseObjectJson(dadosDoMunicipio.dados)

  return montarResultado(dados, tipoResultado)
}

export function percentualEquipe(
  dadosDaTabelaResultado,
  nuIne,
  tipoResultado = 'quadrimestral'
) {
  const mesAnoAtual = getMesAnoAtual()

  const dadosDaEquipe = dadosDaTabelaResultado.find(
    (item) =>
      item.nivel == 2 &&
      item.ine === nuIne &&
      item.indicador == 44 &&
      item.mes_ano === mesAnoAtual
  )

  if (!dadosDaEquipe) {
    return {
      numerador: 0,
      denominador: 0,
      resultado: 0,
      classificacao: '',
    }
  }

  const dados = utils.methods.parseObjectJson(dadosDaEquipe.dados)

  return montarResultado(dados, tipoResultado)
}

export function percentualMicroArea(
  dadosDaTabelaResultado,
  nu_ine,
  micro_area,
  tipoResultado = 'quadrimestral'
) {
  const mesAnoAtual = getMesAnoAtual()

  const dadosDaMicroArea = dadosDaTabelaResultado.find(
    (item) =>
      item.nivel == 3 &&
      item.ine == nu_ine &&
      item.micro_area === micro_area &&
      item.indicador == 44 &&
      item.mes_ano === mesAnoAtual
  )

  if (!dadosDaMicroArea) {
    return {
      numerador: 0,
      denominador: 0,
      resultado: 0,
      classificacao: '',
    }
  }

  const dados = utils.methods.parseObjectJson(dadosDaMicroArea.dados)

  return montarResultado(dados, tipoResultado)
}
